import React, { useState, useEffect } from "react";
import { Box, Button, IconButton, Alert, useMediaQuery, useTheme } from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import TabletTablesSection from "../../components/PagesComponents/PosSection/PosPageTaps/TablesSectionTap/TabletTablesSection";
import TabletDeliverySection from "../../components/PagesComponents/PosSection/PosPageTaps/DeliverySectionTap/TabletDeliverySection";
import TabletTakeawaySection from "../../components/PagesComponents/PosSection/PosPageTaps/TakeawaySectionTap/TabletTakeawaySection";
import ReservationSection from "../../components/PagesComponents/PosSection/PosPageTaps/ReservationSectionTap/ReservationSection";
import { useFeatures } from "../../context/PermissionsAndFeatures/FeaturesContext";

const POS = ({ isSidebarOpen }) => {
  const features = useFeatures();
  const theme = useTheme();
  
  // ✅ Width-based breakpoints only (no height checks)
  const isSmallTablet = useMediaQuery("(min-width: 600px) and (max-width: 768px)");
  const isMediumTablet = useMediaQuery("(min-width: 769px) and (max-width: 900px)");
  const isLargeTablet = useMediaQuery("(min-width: 901px) and (max-width: 1024px)");
  
  // ✅ Orientation only (no height-based sizing)
  const isPortrait = useMediaQuery("(orientation: portrait)");

  // ✅ Simplified responsive sizing
  const getResponsiveValue = (small, medium, large) => {
    if (isSmallTablet) return small;
    if (isMediumTablet) return medium;
    return large;
  };

  const buttonSize = getResponsiveValue(
    { fontSize: "12px", padding: "8px 14px", minWidth: "auto", height: 44 },
    { fontSize: "13px", padding: "10px 16px", minWidth: "120px", height: 46 },
    { fontSize: "14px", padding: "12px 20px", minWidth: "140px", height: 48 }
  );

  const iconButtonSize = getResponsiveValue(40, 44, 48);
  const iconSize = getResponsiveValue(18, 20, 22);
  const tabBarGap = getResponsiveValue(0.8, 1, 1.5);
  const contentPadding = getResponsiveValue(1.5, 2, 2.5);

  // helper to resolve feature flag safely
  const resolveFeature = (featObj, key) => {
    if (featObj == null) return null;
    try {
      if (typeof featObj === "function") return featObj(key);
      if (typeof featObj.isEnabled === "function") return featObj.isEnabled(key);
      if (typeof featObj.hasFeature === "function") return featObj.hasFeature(key);
      if (typeof featObj.get === "function") return featObj.get(key);
      if (key in featObj) return featObj[key];
    } catch (e) {
      return null;
    }
    return null;
  };

  const isTakeawayEnabled = resolveFeature(features, "operation_takeaway") !== false;
  const isDeliveryEnabled = resolveFeature(features, "operation_delivery") !== false;
  const isTableEnabled = resolveFeature(features, "operation_table") !== false;

  // initialize tabIndex from localStorage (unchanged behavior)
  const [tabIndex, setTabIndex] = useState(() => {
    const savedTabIndex = localStorage.getItem("posTabIndex");
    return savedTabIndex ? parseInt(savedTabIndex, 10) : 0;
  });

  useEffect(() => {
    localStorage.setItem("posTabIndex", tabIndex);
  }, [tabIndex]);

  // build tabs list: TAKEAWAY always visible now
  const tabs = [
    { id: "tables", label: "الطاولات", url: "/tables" },
    { id: "delivery", label: "الدليفري", url: "/delivery" },
    { id: "takeaway", label: "التيك أواي", url: "/takeaway" },
    { id: "reservation", label: "الحجز", url: "/reservation" },
  ];

  // if saved index is out of bounds (e.g. feature toggled off), clamp it
  useEffect(() => {
    if (tabIndex >= tabs.length) {
      setTabIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs.length]);

  const handleChange = (index) => {
    setTabIndex(index);
  };

  const openInNewTab = () => {
    const tab = tabs[tabIndex];
    if (!tab) return;

    // if opening tables and feature disabled -> show friendly message in new tab
    if (tab.id === "tables" && !isTableEnabled) {
      const win = window.open("", "_blank");
      if (!win) return;
      win.document.write(`
        <html>
          <head><title>الطاولات - غير مفعّل</title></head>
          <body style="font-family: sans-serif; display:flex; align-items:center; justify-content:center; height:100vh; background:#fff;">
            <div style="max-width:620px; padding:20px; border-radius:8px; box-shadow:0 4px 18px rgba(0,0,0,0.06); text-align:center;">
              <h2 style="margin-bottom:8px;">🚫 خدمة الطاولات غير مفعّلة</h2>
              <p style="color:#333; line-height:1.5;">يُرجى التواصل مع فريق الدعم الفني لتفعيل ميزة الطاولات.</p>
            </div>
          </body>
        </html>
      `);
      win.document.close();
      return;
    }

    // if opening takeaway and feature disabled -> show friendly message in new tab
    if (tab.id === "takeaway" && !isTakeawayEnabled) {
      const win = window.open("", "_blank");
      if (!win) return;
      win.document.write(`
        <html>
          <head><title>التيك أواي - غير مفعّل</title></head>
          <body style="font-family: sans-serif; display:flex; align-items:center; justify-content:center; height:100vh; background:#fff;">
            <div style="max-width:620px; padding:20px; border-radius:8px; box-shadow:0 4px 18px rgba(0,0,0,0.06); text-align:center;">
              <h2 style="margin-bottom:8px;">🚫 خدمة التيك أواي غير مفعّلة</h2>
              <p style="color:#333; line-height:1.5;">يُرجى التواصل مع فريق الدعم الفني لتفعيل ميزة التيك أواي.</p>
            </div>
          </body>
        </html>
      `);
      win.document.close();
      return;
    }

    // if opening delivery and feature disabled -> show friendly message in new tab
    if (tab.id === "delivery" && !isDeliveryEnabled) {
      const win = window.open("", "_blank");
      if (!win) return;
      win.document.write(`
        <html>
          <head><title>الدليفري - غير مفعّل</title></head>
          <body style="font-family: sans-serif; display:flex; align-items:center; justify-content:center; height:100vh; background:#fff;">
            <div style="max-width:620px; padding:20px; border-radius:8px; box-shadow:0 4px 18px rgba(0,0,0,0.06); text-align:center;">
              <h2 style="margin-bottom:8px;">🚫 خدمة الدليفري غير مفعّلة</h2>
              <p style="color:#333; line-height:1.5;">يُرجى التواصل مع فريق الدعم الفني لتفعيل ميزة الدليفري.</p>
            </div>
          </body>
        </html>
      `);
      win.document.close();
      return;
    }

    // normal behavior
    window.open(tab.url, "_blank");
  };

  // render content based on active tab id
  const renderTabContent = (tabId) => {
    switch (tabId) {
      case "tables":
        if (!isTableEnabled) {
          return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
              <Alert severity="warning" sx={{ maxWidth: 720, textAlign: "center" }}>
                🚫 خدمة الطاولات غير مفعّلة حالياً. يرجى التواصل مع فريق الدعم الفني لتفعيل الميزة.
              </Alert>
            </Box>
          );
        }
        return <TabletTablesSection />;
      case "delivery":
        if (!isDeliveryEnabled) {
          return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
              <Alert severity="warning" sx={{ maxWidth: 720, textAlign: "center" }}>
                🚫 خدمة الدليفري غير مفعّلة حالياً. يرجى التواصل مع فريق الدعم الفني لتفعيل الميزة.
              </Alert>
            </Box>
          );
        }
        return <TabletDeliverySection />;
      case "takeaway":
        if (!isTakeawayEnabled) {
          return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
              <Alert severity="warning" sx={{ maxWidth: 720, textAlign: "center" }}>
                🚫 خدمة التيك أواي غير مفعّلة حالياً. يرجى التواصل مع فريق الدعم الفني لتفعيل الميزة.
              </Alert>
            </Box>
          );
        }
        return <TabletTakeawaySection />;
      case "reservation":
        return <ReservationSection />;
      default:
        return null;
    }
  };

  return (
    <Box 
      sx={{ 
        // ✅ ROOT CONTAINER: Flexbox fills viewport without overflow issues
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100dvh", // Modern dynamic viewport height
        minHeight: 0, // Critical for flex children
        overflow: "hidden", // Prevent document scroll
        position: "relative",
      }}
    >
      {/* ✅ TAB BAR: Fixed height, no flex-grow */}
      <Box
        sx={{
          flexShrink: 0, // Don't shrink
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: tabBarGap,
          backgroundColor: "transparent",
          padding: isSmallTablet ? "10px 8px" : isMediumTablet ? "12px 10px" : "14px 12px",
          flexWrap: isPortrait && isSmallTablet ? "wrap" : "nowrap",
          overflowX: "auto",
          overflowY: "hidden",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(0,0,0,0.2) rgba(0,0,0,0.05)",
          "&::-webkit-scrollbar": {
            height: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.2)",
            borderRadius: "4px",
          },
        }}
      >
        {tabs.map((tab, index) => (
          <Button
            key={tab.id}
            onClick={() => handleChange(index)}
            sx={{
              backgroundColor: tabIndex === index ? "#814C3B" : "#EA7C69",
              color: "#fff",
              fontSize: buttonSize.fontSize,
              fontWeight: "bold",
              borderRadius: "8px",
              padding: buttonSize.padding,
              minWidth: buttonSize.minWidth,
              minHeight: buttonSize.height,
              flex: isPortrait && isSmallTablet ? "1 1 calc(50% - 8px)" : "0 0 auto",
              whiteSpace: "nowrap",
              textTransform: "none",
              transition: "all 0.25s",
              boxShadow: tabIndex === index 
                ? "0 2px 8px rgba(129, 76, 59, 0.3)" 
                : "0 2px 6px rgba(234, 124, 105, 0.2)",
              touchAction: "manipulation",
              WebkitTapHighlightColor: "transparent",
              "&:hover": {
                backgroundColor: tabIndex === index ? "#6E3A2F" : "#D97B5A",
                transform: "translateY(-2px)",
                boxShadow: tabIndex === index
                  ? "0 4px 14px rgba(129, 76, 59, 0.4)"
                  : "0 4px 12px rgba(234, 124, 105, 0.3)",
              },
              "&:active": {
                transform: "translateY(0px)",
              },
            }}
          >
            {tab.label}
          </Button>
        ))}

        <IconButton
          color="primary"
          onClick={openInNewTab}
          title="فتح في تبويب جديد"
          sx={{
            backgroundColor: "#EA7C69",
            color: "#fff",
            borderRadius: "8px",
            minWidth: iconButtonSize,
            minHeight: iconButtonSize,
            width: iconButtonSize,
            height: iconButtonSize,
            flexShrink: 0,
            transition: "all 0.25s",
            boxShadow: "0 2px 6px rgba(234, 124, 105, 0.2)",
            touchAction: "manipulation",
            "&:hover": {
              backgroundColor: "#D97B5A",
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(234, 124, 105, 0.3)",
            },
            "& svg": {
              fontSize: iconSize,
            },
          }}
        >
          <OpenInNew />
        </IconButton>
      </Box>

      {/* ✅ CONTENT AREA: Flex-grow fills remaining space, single scroll container */}
      <Box
        sx={{
          flex: 1, // Fills remaining vertical space
          minHeight: 0, // Critical for scroll to work
          overflowY: "auto",
          overflowX: "hidden",
          padding: contentPadding,
          scrollBehavior: "smooth",
          WebkitOverflowScrolling: "touch",
          // ✅ Custom scrollbar
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(0,0,0,0.2) rgba(0,0,0,0.05)",
          "&::-webkit-scrollbar": {
            width: isSmallTablet ? "4px" : "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "rgba(0,0,0,0.05)",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.2)",
            borderRadius: "6px",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.3)",
            },
          },
        }}
      >
        {renderTabContent(tabs[tabIndex]?.id)}
      </Box>

      {/* ✅ Global styles - width-based only, no height checks */}
      <style>
        {`
          /* Reset any problematic body/html styles */
          html, body, #root {
            margin: 0;
            padding: 0;
            width: 100%;
            height: 100%;
            overflow: hidden;
          }

          /* Width-based tablet breakpoints */
          @media (min-width: 600px) and (max-width: 768px) {
            body {
              font-size: 13px;
              -webkit-text-size-adjust: 100%;
            }
          }

          @media (min-width: 769px) and (max-width: 900px) {
            body {
              font-size: 14px;
              -webkit-text-size-adjust: 100%;
            }
          }

          @media (min-width: 901px) and (max-width: 1024px) {
            body {
              font-size: 15px;
              -webkit-text-size-adjust: 100%;
            }
          }

          /* Touch device optimizations */
          @media (hover: none) and (pointer: coarse) {
            *::-webkit-scrollbar {
              width: 0;
              height: 0;
            }
            
            button, a, input, select, textarea {
              min-height: 44px;
              min-width: 44px;
            }
          }

          /* General tablet optimizations */
          @media (min-width: 600px) and (max-width: 1024px) {
            * {
              -webkit-tap-highlight-color: transparent;
            }
            
            body {
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default POS;
